import { useMemo, useState, useCallback, useRef } from "react";
import { Box, Card, CardContent, Stack, Typography, TextField, Tooltip } from "@mui/material";
import { ResponsiveContainer, Sankey } from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle, faFilter, faUndo, faCircleInfo, faCalendarDays } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import config from "../../config";
import { isMasterAdmin } from "../../utils/auth";
import ActionButton from "../ui/ActionButton";

const PALETTE = [
  "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
  "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf",
  "#4e79a7", "#f28e2b", "#59a14f", "#e15759", "#b07aa1",
  "#9c755f", "#edc948", "#76b7b2", "#af7aa1", "#bab0ac",
];

const PRESET_KEYS = ["thisMonth", "lastMonth", "thisQuarter", "thisYear", "lastYear", "allTime"];

// preset range calculator
const getPresetRange = (preset) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const quarter = Math.floor(month / 3);

  switch (preset) {
    case "thisMonth":
      return { start: new Date(year, month, 1), end: new Date(year, month + 1, 0) };
    case "lastMonth":
      return { start: new Date(year, month - 1, 1), end: new Date(year, month, 0) };
    case "thisQuarter":
      return { start: new Date(year, quarter * 3, 1), end: new Date(year, quarter * 3 + 3, 0) };
    case "thisYear":
      return { start: new Date(year, 0, 1), end: new Date(year, 11, 31) };
    case "lastYear":
      return { start: new Date(year - 1, 0, 1), end: new Date(year - 1, 11, 31) };
    case "allTime":
    default:
      return { start: null, end: null };
  }
};

const hashStr = (s) => {
  const str = String(s ?? "");
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
};

const hexToRgba = (hex, a) => {
  const h = String(hex).replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const isImpactNode = (name) => name === "Quantity" || String(name ?? "").startsWith("Impact (");

const formatNum = (n) => n?.toLocaleString() ?? "0";

// convert Date to input format (YYYY-MM-DD)
const toInputDate = (d) => (d ? d.toISOString().split("T")[0] : "");

const materialColor = (material) => PALETTE[hashStr(material) % PALETTE.length];

// get material from link payload (for coloring)
const getMaterial = (payload) => {
  const srcName = payload?.source?.name ?? "";
  const tgtName = payload?.target?.name ?? "";
  if (!isImpactNode(tgtName) && !isImpactNode(srcName)) return srcName; // material to receiver
  if (isImpactNode(tgtName) && !isImpactNode(srcName)) return srcName; // receiver to impact (use receiver as proxy)
  return tgtName || srcName || "Unknown";
};

export default function SankeyPanel({ 
  initialReceiverBreakdown,
  initialMaterialBreakdown,
  initialTotalQty,
  initialTotalAvoided,
  darkMode, 
  pagePreset,
  scrollTargetRef,
}) {
  const { t } = useTranslation();
  const [appliedPreset, setAppliedPreset] = useState(pagePreset || "allTime");
  const [pendingPreset, setPendingPreset] = useState(pagePreset || "allTime");
  const [pendingDateFrom, setPendingDateFrom] = useState("");
  const [pendingDateTo, setPendingDateTo] = useState("");
  const [appliedDateFrom, setAppliedDateFrom] = useState("");
  const [appliedDateTo, setAppliedDateTo] = useState("");
  const [receiverBreakdown, setReceiverBreakdown] = useState(initialReceiverBreakdown || []);
  const [materialBreakdown, setMaterialBreakdown] = useState(initialMaterialBreakdown || []);
  const [totalQty, setTotalQty] = useState(initialTotalQty || 0);
  const [totalAvoided, setTotalAvoided] = useState(initialTotalAvoided || 0);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [hasLocalFilter, setHasLocalFilter] = useState(false);
  const [animationKey, setAnimationKey] = useState(0); // triggers re-animation

  const scrollToPanel = useCallback(() => {
    setTimeout(() => {
      if (scrollTargetRef?.current) {
        scrollTargetRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 50);
  }, [scrollTargetRef]);
  const [activeChart, setActiveChart] = useState("qty"); // "qty" or "ghg"

  const textColor = darkMode ? "#e6eef5" : "#1b2a36";
  const nodeFill = darkMode ? "#24313a" : "#e8eef3";
  const nodeStroke = darkMode ? "#3c4f5c" : "#c2d2dd";

  const hasPendingChanges = 
    pendingPreset !== appliedPreset ||
    pendingDateFrom !== appliedDateFrom ||
    pendingDateTo !== appliedDateTo;

  // compute a human-readable period summary
  const periodSummary = useMemo(() => {
    const monthNames = [
      t("Jan"), t("Feb"), t("Mar"), t("Apr"), t("May"), t("Jun"),
      t("Jul"), t("Aug"), t("Sep"), t("Oct"), t("Nov"), t("Dec"),
    ];

    let start, end;
    if (appliedPreset === "custom") {
      start = appliedDateFrom ? new Date(appliedDateFrom + "T00:00:00") : null;
      end = appliedDateTo ? new Date(appliedDateTo + "T00:00:00") : null;
    } else {
      const range = getPresetRange(appliedPreset);
      start = range.start;
      end = range.end;
    }

    if (!start && !end) {
      return { label: t("All Time"), duration: null };
    }

    const fmt = (d) => `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
    const label = start && end
      ? `${fmt(start)} – ${fmt(end)}`
      : start ? `${t("From")} ${fmt(start)}` : `${t("Until")} ${fmt(end)}`;

    // calculate duration
    let duration = null;
    if (start && end) {
      const diffMs = end.getTime() - start.getTime();
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
      if (diffDays <= 31) {
        duration = `${diffDays} ${t("days")}`;
      } else if (diffDays <= 90) {
        const weeks = Math.round(diffDays / 7);
        duration = `${weeks} ${t("weeks")}`;
      } else {
        const months = Math.round(diffDays / 30.44);
        duration = `${months} ${t("months")}`;
      }
    }

    return { label, duration };
  }, [appliedPreset, appliedDateFrom, appliedDateTo, t]);

  // fetch data independently when local preset changes
  const fetchSankeyData = useCallback(async (preset, dateFrom, dateTo) => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const params = new URLSearchParams();
      
      if (preset === "custom") {
        if (dateFrom) params.append("date_from", dateFrom);
        if (dateTo) params.append("date_to", dateTo);
      } else {
        const range = getPresetRange(preset);
        if (range.start) {
          params.append("date_from", range.start.toISOString().split("T")[0]);
        }
        if (range.end) {
          params.append("date_to", range.end.toISOString().split("T")[0]);
        }
      }
      
      if (isMasterAdmin()) {
        params.append("show_all", "true");
      }

      const url = `${config.apiUrl}/donor/ghg/summary?${params.toString()}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        credentials: "include",
      });

      const result = await response.json();
      if (response.ok && result.success) {
        const breakdown = result.receiverBreakdown || [];
        setReceiverBreakdown(breakdown);
        setMaterialBreakdown(result.materialBreakdown || []);
        setTotalAvoided(result.totalAvoided || 0);
        setTotalQty(breakdown.reduce((sum, r) => sum + (r.totalQty || 0), 0));
        setAppliedPreset(preset);
        setAppliedDateFrom(dateFrom || "");
        setAppliedDateTo(dateTo || "");
        setAnimationKey(k => k + 1); // trigger re-animation
      } else {
        setFetchError(t("Failed to load data. Please try again."));
      }
    } catch (err) {
      console.error("Error fetching sankey data:", err);
      setFetchError(t("Failed to load data. Please try again."));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  // update pending preset and populate date fields (no fetch until Apply)
  const handlePresetClick = useCallback((preset) => {
    setPendingPreset(preset);
    const range = getPresetRange(preset);
    setPendingDateFrom(range.start ? toInputDate(range.start) : "");
    setPendingDateTo(range.end ? toInputDate(range.end) : "");
  }, []);

  // handle custom date change
  const handleDateChange = useCallback((field, value) => {
    setPendingPreset("custom");
    if (field === "from") setPendingDateFrom(value);
    else setPendingDateTo(value);
  }, []);

  // apply the pending preset
  const handleApply = useCallback(() => {
    setHasLocalFilter(true);
    fetchSankeyData(pendingPreset, pendingDateFrom, pendingDateTo);
  }, [fetchSankeyData, pendingPreset, pendingDateFrom, pendingDateTo]);

  // reset to page data
  const handleReset = useCallback(() => {
    setHasLocalFilter(false);
    setReceiverBreakdown(initialReceiverBreakdown || []);
    setMaterialBreakdown(initialMaterialBreakdown || []);
    setTotalQty(initialTotalQty || 0);
    setTotalAvoided(initialTotalAvoided || 0);
    setAppliedPreset(pagePreset || "allTime");
    setPendingPreset(pagePreset || "allTime");
    setPendingDateFrom("");
    setPendingDateTo("");
    setAppliedDateFrom("");
    setAppliedDateTo("");
    setAnimationKey(k => k + 1);
  }, [initialReceiverBreakdown, initialMaterialBreakdown, initialTotalQty, initialTotalAvoided, pagePreset]);

  // transform data into sankey format
  const sankeyData = useMemo(() => {
    const TOP_RECEIVERS = 12;
    const TOP_MATERIALS = 14;

    if (!receiverBreakdown.length) return { qty: { nodes: [], links: [] }, avoided: { nodes: [], links: [] }, groupedMaterials: [], groupedReceivers: [] };

    const topReceivers = new Set(
      receiverBreakdown.slice(0, TOP_RECEIVERS).map((r) => r.name)
    );
    const topMaterials = new Set(
      materialBreakdown.slice(0, TOP_MATERIALS).map((m) => m.name)
    );

    // track what got grouped
    const groupedMaterialSet = new Set();
    const groupedReceiverSet = new Set();

    const receiverLabel = (name) => {
      if (topReceivers.has(name)) return name;
      groupedReceiverSet.add(name);
      return "Other receivers";
    };
    const materialLabel = (name) => {
      if (topMaterials.has(name)) return name;
      groupedMaterialSet.add(name);
      return "Other materials";
    };

    const qtyLinks = new Map();
    const avoidedLinks = new Map();

    const addLink = (map, src, tgt, val) => {
      if (!Number.isFinite(val) || val <= 0) return;
      const k = `${src}|||${tgt}`;
      map.set(k, (map.get(k) || 0) + val);
    };

    for (const r of receiverBreakdown) {
      const recLabel = receiverLabel(r.name);
      const mats = r.materials || [];
      const matCount = mats.length || 1;
      const qtyPerMat = (r.totalQty || 0) / matCount;
      const avoidedPerMat = (r.avoided || 0) / matCount;

      for (const mat of mats) {
        const matLabel = materialLabel(mat);
        addLink(qtyLinks, matLabel, recLabel, qtyPerMat);
        addLink(avoidedLinks, matLabel, recLabel, avoidedPerMat);
      }

      if (!mats.length) {
        addLink(qtyLinks, "Other materials", recLabel, r.totalQty || 0);
        addLink(avoidedLinks, "Other materials", recLabel, r.avoided || 0);
      }

      addLink(qtyLinks, recLabel, "Quantity", r.totalQty || 0);
      addLink(avoidedLinks, recLabel, "Impact (kg CO₂e)", r.avoided || 0);
    }

    const buildChart = (linkMap) => {
      const nodeSet = new Set();
      const links = [];
      
      for (const [k, v] of linkMap.entries()) {
        if (!Number.isFinite(v) || v <= 0) continue;
        const [src, tgt] = k.split("|||");
        nodeSet.add(src);
        nodeSet.add(tgt);
        links.push({ src, tgt, value: v });
      }
      
      const nodes = [...nodeSet].map(name => ({ name }));
      const nodeIndex = new Map(nodes.map((n, i) => [n.name, i]));
      
      const indexedLinks = links.map(l => ({
        source: nodeIndex.get(l.src),
        target: nodeIndex.get(l.tgt),
        value: l.value,
      }));
      
      return { nodes, links: indexedLinks };
    };

    return {
      qty: buildChart(qtyLinks),
      avoided: buildChart(avoidedLinks),
      groupedMaterials: [...groupedMaterialSet].sort(),
      groupedReceivers: [...groupedReceiverSet].sort(),
    };
  }, [receiverBreakdown, materialBreakdown]);

  // find top GHG impact paths for caption (excludes "Other materials" and "Other receivers")
  const topImpactPaths = useMemo(() => {
    if (!sankeyData?.avoided?.links?.length) return [];

    const nodes = sankeyData.avoided.nodes || [];
    const paths = [];

    for (const link of sankeyData.avoided.links) {
      const srcName = nodes[link.source]?.name || "";
      const tgtName = nodes[link.target]?.name || "";

      if (!isImpactNode(srcName) && !isImpactNode(tgtName) && 
          srcName !== "Other materials" && tgtName !== "Other receivers") {
        paths.push({ material: srcName, receiver: tgtName, value: link.value });
      }
    }

    // sort by value descending and take top 3
    return paths.sort((a, b) => b.value - a.value).slice(0, 3);
  }, [sankeyData]);

  // find top quantity paths for caption (excludes "Other materials" and "Other receivers")
  const topQuantityPaths = useMemo(() => {
    if (!sankeyData?.qty?.links?.length) return [];

    const nodes = sankeyData.qty.nodes || [];
    const paths = [];

    for (const link of sankeyData.qty.links) {
      const srcName = nodes[link.source]?.name || "";
      const tgtName = nodes[link.target]?.name || "";
      if (!isImpactNode(srcName) && !isImpactNode(tgtName) && 
          srcName !== "Other materials" && tgtName !== "Other receivers") {
        paths.push({ material: srcName, receiver: tgtName, value: link.value });
      }
    }

    // sort by value descending and take top 3
    return paths.sort((a, b) => b.value - a.value).slice(0, 3);
  }, [sankeyData]);

  // check concentration risk
  const concentrationWarning = useMemo(() => {
    if (!sankeyData?.qty?.links?.length || !totalQty) return null;

    const nodes = sankeyData.qty.nodes || [];
    const receiverTotals = new Map();

    for (const link of sankeyData.qty.links) {
      const srcName = nodes[link.source]?.name || "";
      const tgtName = nodes[link.target]?.name || "";
      // material to receiver links
      if (!isImpactNode(srcName) && !isImpactNode(tgtName)) {
        receiverTotals.set(tgtName, (receiverTotals.get(tgtName) || 0) + link.value);
      }
    }

    let maxReceiver = null;
    let maxQty = 0;
    for (const [name, qty] of receiverTotals) {
      if (qty > maxQty) {
        maxQty = qty;
        maxReceiver = name;
      }
    }

    if (!maxReceiver) return null;
    const pct = (maxQty / totalQty) * 100;

    if (pct >= 80) return { receiver: maxReceiver, pct: Math.round(pct), level: "high" };
    if (pct >= 50) return { receiver: maxReceiver, pct: Math.round(pct), level: "moderate" };
    return null;
  }, [sankeyData, totalQty]);

  // node index counter for staggered animation
  const nodeIndexRef = useRef(0);

  const SankeyNode = useCallback((props) => {
    const { x, y, width, height, payload } = props;
    
    // use minimum height of 4px so tiny nodes still connect visually
    const minHeight = 4;
    const renderHeight = Math.max(minHeight, height);
    const yOffset = height < minHeight ? (minHeight - height) / 2 : 0;
    const centerY = y - yOffset + renderHeight / 2;

    const nodeIndex = nodeIndexRef.current++;
    const nodeDelay = Math.min(nodeIndex * 0.04, 1.2);
    const labelDelay = nodeDelay + 0.15;

    const name = payload?.name ?? "";
    const isImpact = isImpactNode(name);
    const anchorRight = x < 200; // adjusted for reduced margins

    // improve end node display names
    let displayName = name;
    if (name === "Quantity") {
      displayName = t("Total Donated (lbs)");
    } else if (name === "Impact (kg CO₂e)") {
      displayName = t("GHG Impact (kg CO₂e)");
    }
    const label = displayName.length > 32 ? `${displayName.slice(0, 32)}…` : displayName;

    // build descriptive tooltip based on node type
    let tooltip = name;
    if (name === "Quantity") {
      tooltip = `${t("Total Donated (lbs)")}: ${formatNum(Math.round(totalQty))} lbs`;
    } else if (name === "Impact (kg CO₂e)") {
      tooltip = `${t("GHG Impact (kg CO₂e)")}: ${formatNum(Math.round(totalAvoided))} kg CO₂e`;
    } else if (name === "Other materials" && sankeyData.groupedMaterials?.length) {
      tooltip = `${t("Other materials")} (${sankeyData.groupedMaterials.length}):\n${sankeyData.groupedMaterials.join(", ")}`;
    } else if (name === "Other receivers" && sankeyData.groupedReceivers?.length) {
      tooltip = `${t("Other receivers")} (${sankeyData.groupedReceivers.length}):\n${sankeyData.groupedReceivers.join(", ")}`;
    }

    return (
      <g className="sankey-node">
        <rect
          x={x}
          y={y - yOffset}
          width={Math.max(3, width)}
          height={renderHeight}
          fill={isImpact ? (darkMode ? "#1e3a4b" : "#cfe7f6") : nodeFill}
          stroke={nodeStroke}
          strokeWidth={1}
          rx={2}
          className="sankey-node-rect"
          style={{
            animation: `sankeyNodeFadeIn 0.4s ease-out ${nodeDelay}s both`,
          }}
        >
          <title>{tooltip}</title>
        </rect>
        <text
          x={anchorRight ? x + width + 10 : x - 10}
          y={centerY}
          textAnchor={anchorRight ? "start" : "end"}
          dominantBaseline="middle"
          fontSize={12}
          fill={textColor}
          className="sankey-node-label"
          style={{ 
            pointerEvents: "none",
            animation: `sankeyLabelFadeIn 0.5s ease-out ${labelDelay}s both`,
          }}
        >
          {label}
        </text>
      </g>
    );
  }, [darkMode, nodeFill, nodeStroke, textColor, totalQty, totalAvoided, sankeyData.groupedMaterials, sankeyData.groupedReceivers, t]);

  // link index counter for staggered animation
  const linkIndexRef = useRef(0);

  const SankeyLink = useCallback((props) => {
    const { sourceX, sourceY, targetX, targetY, sourceControlX, targetControlX, linkWidth, payload } = props;
    if (linkWidth < 0.6) return null;

    const linkIndex = linkIndexRef.current++;
    const animDelay = Math.min(linkIndex * 0.03, 1.5);

    const d = `
      M ${sourceX},${sourceY}
      C ${sourceControlX},${sourceY}
        ${targetControlX},${targetY}
        ${targetX},${targetY}
    `;

    const srcName = payload?.source?.name ?? "";
    const tgtName = payload?.target?.name ?? "";
    const material = getMaterial(payload);
    const base = materialColor(material);
    const alpha = darkMode ? 0.55 : 0.40;
    const stroke = hexToRgba(base, alpha);

    const value = Number(payload?.value ?? 0);
    const isTargetImpact = isImpactNode(tgtName);
    const isGHGImpact = isTargetImpact && String(tgtName).startsWith("Impact (");
    
    let tooltipLines = [];
    
    if (isTargetImpact) {
      // receiver to impact link
      const pct = isGHGImpact 
        ? (totalAvoided ? ((value / totalAvoided) * 100).toFixed(1) : 0)
        : (totalQty ? ((value / totalQty) * 100).toFixed(1) : 0);
      tooltipLines = [
        srcName,
        isGHGImpact 
          ? `${formatNum(Math.round(value))} ${t("kg CO₂e avoided")}`
          : `${formatNum(Math.round(value))} ${t("lbs donated")}`,
        `${pct}% ${t("of total")}`,
      ];
    } else {
      // material to receiver link
      tooltipLines = [
        `${srcName} → ${tgtName}`,
        `${formatNum(Math.round(value))} ${t("lbs")}`,
      ];
    }

    return (
      <path 
        d={d} 
        fill="none" 
        stroke={stroke} 
        strokeWidth={Math.max(1, linkWidth)} 
        strokeLinecap="butt"
        className="sankey-link-animated"
        style={{
          strokeDasharray: 1000,
          strokeDashoffset: 1000,
          animation: `sankeyLinkDraw 0.8s ease-out ${animDelay}s forwards`,
        }}
      >
        <title>{tooltipLines.join("\n")}</title>
      </path>
    );
  }, [darkMode, totalQty, totalAvoided, t]);

  const empty = !sankeyData?.qty?.links?.length && !sankeyData?.avoided?.links?.length;

  if (empty) {
    return (
      <Typography variant="body2" sx={{ opacity: 0.75, py: 2 }}>
        {t("No delivered data for the selected period.")}
      </Typography>
    );
  }

  // reset index counters before each render
  nodeIndexRef.current = 0;
  linkIndexRef.current = 0;

  return (
    <Stack spacing={2}>
      {/* Chart toggle tabs */}
      <div className="sankey-chart-tabs">
        <button
          className={`sankey-chart-tab ${activeChart === "qty" ? "active" : ""}`}
          onClick={() => { setActiveChart("qty"); setAnimationKey(k => k + 1); scrollToPanel(); }}
        >
          {t("Quantity Flow")}
        </button>
        <button
          className={`sankey-chart-tab ${activeChart === "ghg" ? "active" : ""}`}
          onClick={() => { setActiveChart("ghg"); setAnimationKey(k => k + 1); scrollToPanel(); }}
        >
          {t("GHG Impact Flow")}
        </button>
      </div>

      {/* Chart + Side Panel layout */}
      <div className="sankey-chart-layout">
        <div className="sankey-chart-main">
          <Card variant="outlined" className="ghg-sankey-card">
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box className="ghg-sankey-chart" sx={{ width: "100%", height: { xs: 420, md: 520, lg: 600 } }} key={`${activeChart}-${animationKey}`}>
                <ResponsiveContainer width="100%" height="100%">
                  <Sankey
                    data={activeChart === "qty" ? sankeyData.qty : sankeyData.avoided}
                    nodePadding={30}
                    nodeWidth={22}
                    iterations={36}
                    node={<SankeyNode />}
                    link={<SankeyLink />}
                    margin={{ top: 12, right: 180, bottom: 12, left: 140 }}
                  />
                </ResponsiveContainer>
              </Box>
              <Box className="sankey-caption" sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {activeChart === "qty" 
                    ? t("Flow thickness = delivered quantity. Link color = material type.")
                    : t("Flow thickness = emissions impact (quantity × emission factor). Link color = material type.")
                  }
                </Typography>
                <Tooltip 
                  title={t("Flow totals may differ slightly from summary figures due to rounding at the receiver level.")}
                  arrow
                  placement="top"
                >
                  <span style={{ cursor: "help", opacity: 0.5 }}>
                    <FontAwesomeIcon icon={faCircleInfo} style={{ fontSize: "0.75rem" }} />
                  </span>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        </div>

        {/* Side Panel */}
        <div className="sankey-side-panel">
          {/* Period summary badge */}
          <div className="sankey-side-period-badge" style={{ animationDelay: "0s" }}>
            <FontAwesomeIcon icon={faCalendarDays} className="period-badge-icon" />
            <div className="period-badge-text">
              <span className="period-badge-label">{periodSummary.label}</span>
              {periodSummary.duration && (
                <span className="period-badge-duration">{periodSummary.duration}</span>
              )}
            </div>
          </div>

          {/* Top paths */}
          {activeChart === "qty" && topQuantityPaths.length > 0 && (
            <div className="sankey-side-section side-card-cascade" style={{ animationDelay: "0.15s" }} key={`qty-paths-${animationKey}`}>
              <div className="sankey-side-title">{t("Top donation paths")}</div>
              {topQuantityPaths.map((path, idx) => (
                <div 
                  key={`qty-${path.material}-${path.receiver}`} 
                  className="sankey-side-path"
                  style={{ animationDelay: `${idx * 0.08}s` }}
                >
                  <span className="side-path-rank">{idx + 1}</span>
                  <div className="side-path-detail">
                    <div className="side-path-route">
                      <span className="path-material">{path.material}</span>
                      <span className="side-path-arrow">→</span>
                      <span className="side-path-receiver">{path.receiver}</span>
                    </div>
                    <div className="side-path-stats">
                      {formatNum(Math.round(path.value))} {t("lbs")}
                      {totalQty ? ` · ${Math.round((path.value / totalQty) * 100)}%` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeChart === "ghg" && topImpactPaths.length > 0 && (
            <div className="sankey-side-section side-card-cascade" style={{ animationDelay: "0.15s" }} key={`ghg-paths-${animationKey}`}>
              <div className="sankey-side-title">{t("Top impact paths")}</div>
              {topImpactPaths.map((path, idx) => (
                <div 
                  key={`ghg-${path.material}-${path.receiver}`} 
                  className="sankey-side-path"
                  style={{ animationDelay: `${idx * 0.08}s` }}
                >
                  <span className="side-path-rank">{idx + 1}</span>
                  <div className="side-path-detail">
                    <div className="side-path-route">
                      <span className="path-material">{path.material}</span>
                      <span className="side-path-arrow">→</span>
                      <span className="side-path-receiver">{path.receiver}</span>
                    </div>
                    <div className="side-path-stats">
                      {formatNum(Math.round(path.value))} kg CO₂e
                      {totalAvoided ? ` · ${Math.round((path.value / totalAvoided) * 100)}%` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Concentration warning */}
          {concentrationWarning && (
            <div className={`sankey-side-warning ${concentrationWarning.level}`}>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="side-warning-icon"
              />
              <div className="side-warning-text">
                <strong>{concentrationWarning.level === "high" ? t("High concentration") : t("Concentration risk")}</strong>
                <span>{concentrationWarning.receiver} — {concentrationWarning.pct}%</span>
              </div>
            </div>
          )}

          {/* Period filter in side panel */}
          <div className="sankey-side-section sankey-side-filters side-card-cascade" style={{ animationDelay: "0.3s" }}>
            <div className="sankey-side-title">{t("Period")}</div>
            <div className="side-presets-grid">
              {PRESET_KEYS.map((key) => (
                <button
                  key={key}
                  className={`side-preset-btn ${pendingPreset === key ? "active" : ""}`}
                  onClick={() => handlePresetClick(key)}
                  disabled={isLoading}
                >
                  {t(key === "thisMonth" ? "This Month" :
                     key === "lastMonth" ? "Last Month" :
                     key === "thisQuarter" ? "This Quarter" :
                     key === "thisYear" ? "This Year" :
                     key === "lastYear" ? "Last Year" : "All Time")}
                </button>
              ))}
            </div>

            <div className="side-custom-dates">
              <Typography variant="caption" sx={{ color: "var(--text-muted)", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {t("Custom")}
              </Typography>
              <TextField
                type="date"
                size="small"
                value={pendingDateFrom}
                onChange={(e) => handleDateChange("from", e.target.value)}
                className="donor-filter-input ghg-date-input side-date-input"
                inputProps={{ style: { color: darkMode ? "#fff" : "#1a1a1a", fontSize: "0.75rem" } }}
                disabled={isLoading}
              />
              <span className="side-date-sep">{t("to")}</span>
              <TextField
                type="date"
                size="small"
                value={pendingDateTo}
                onChange={(e) => handleDateChange("to", e.target.value)}
                className="donor-filter-input ghg-date-input side-date-input"
                inputProps={{ style: { color: darkMode ? "#fff" : "#1a1a1a", fontSize: "0.75rem" } }}
                disabled={isLoading}
              />
            </div>

            <div className="side-filter-actions">
              {hasLocalFilter && (
                <ActionButton
                  variant="default"
                  icon={faUndo}
                  onClick={handleReset}
                  disabled={isLoading}
                  style={{ fontSize: "0.7rem", padding: "5px 10px" }}
                >
                  {t("Reset")}
                </ActionButton>
              )}
              <ActionButton
                variant="secondary"
                icon={faFilter}
                onClick={handleApply}
                disabled={!hasPendingChanges || isLoading}
                className={isLoading ? "is-loading" : ""}
                style={{ fontSize: "0.7rem", padding: "5px 10px" }}
              >
                {isLoading ? t("Loading...") : t("Apply")}
              </ActionButton>
            </div>
            {fetchError && (
              <Typography variant="caption" sx={{ color: "#ef4444", fontSize: "0.65rem", mt: 0.5 }}>
                {fetchError}
              </Typography>
            )}
          </div>
        </div>
      </div>
    </Stack>
  );
}
